import React, { useCallback, useState, useEffect } from 'react';
import SideActionBoard from '../../../../../components/SideActionBoard';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminAPI, userAPI } from '../../../../../api';
import { convertHubSpotLabel, formatDate } from '../../../../../helpers';
import useAlarm from '../../../../../context/AlarmContext';
import FormLoader from '../../../../../components/FormLoader';
import FormInput from '../../../../../components/FormInput';
import CohortMultiSelect from '../../../../../components/admin/users/invite/CohortMultiSelect';
import UserInviteForm from '../../../../../components/UserInviteForm';
import IndependentUserInviteForm from '../../../../../components/admin/users/IndependentUserInviteForm';
import SVG from 'react-inlinesvg';
import ChevronRightIcon from '../../../../../../../../assets/images/reskin-images/icon--chevrons-right.svg';
import UnlockedIcon from '../../../../../../../../assets/images/reskin-images/icon--unlocked.svg';
import LockedIcon from '../../../../../../../../assets/images/reskin-images/icon--locked.svg';
import CheckIcon from '../../../../../../../../assets/images/reskin-images/icon--check.svg';

const activeHubspotProperties = false;

const SidebarUserEdit = ({ openSideActionBoard, setOpenSideActionBoard, user_id, role }) => {
  const { data: user, isLoading } = useQuery(
    ['getUser', { id: user_id, role: role }],
    userAPI.getUserDetails,
    {
      enabled: !!user_id,
    }
  );
  const [formData, setFormData] = useState({});
  const [availableCohorts, setAvailableCohorts] = useState([]);

  useEffect(() => {
    if (user?.user) {
      setFormData(user.user);
      setAvailableCohorts(user.user.available_cohorts);
    }
  }, [user]);

  if (isLoading) {
    return (
      <SideActionBoard setOpenSideActionBoard={setOpenSideActionBoard}>
        <div className="text-center text-gray-500">Loading...</div>
        <FormLoader />
      </SideActionBoard>
    );
  }

  if (openSideActionBoard?.type === 'user-edit-bulk' && openSideActionBoard?.ids?.length > 0) {
    return (
      <SideActionBoard setOpenSideActionBoard={setOpenSideActionBoard}>
        <div className="flex justify-between items-center relative pb-4 px-8">
          <button
            className="p-2 flex items-center -ml-2"
            type="button"
            onClick={() => setOpenSideActionBoard(false)}
          >
            <SVG src={ChevronRightIcon} />
          </button>
          <span
            style={{ width: 'calc(100% + 6rem)' }}
            className="absolute pin-b pin-r -mx-16 h-px bg-gray flex"
          />
        </div>
        <IndependentUserInviteForm
          items={openSideActionBoard.ids}
          type="cohort-participants"
          title="Edit Users"
          setOpenSideActionBoard={setOpenSideActionBoard}
          setCheckedActiveUsers={() => {}}
          setUnconfirmedUsers={() => {}}
          setPendingInvites={() => {}}
          checkedAllUsers={openSideActionBoard.checkedAllUsers}
          totalUsers={openSideActionBoard.totalUsers}
          cohortsOptions={formData.available_cohorts || []}
          filter={openSideActionBoard.filter}
          searchQuery={openSideActionBoard.searchQuery}
        />
      </SideActionBoard>
    );
  }

  if (openSideActionBoard?.type === 'user-edit' && openSideActionBoard?.user_id) {
    return (
      <SideActionBoard setOpenSideActionBoard={setOpenSideActionBoard}>
        {formData && Object.keys(formData).length > 0 && (
          <UserEditForm
            user={formData}
            openSideActionBoard={openSideActionBoard}
            setOpenSideActionBoard={setOpenSideActionBoard}
            cohorts={availableCohorts || []}
            courses={formData.courses || []}
            alexAccessTypes={formData.alex_access_types || []}
            hubspotAccessTypes={formData.hubspot_access_types || []}
          />
        )}
      </SideActionBoard>
    );
  }

  if (openSideActionBoard?.type === 'user-view' && openSideActionBoard?.user_id) {
    return (
      <SideActionBoard setOpenSideActionBoard={setOpenSideActionBoard}>
        <div className="flex justify-between items-center relative pb-4 px-8">
          <button
            className="p-2 flex items-center -ml-2"
            type="button"
            onClick={() => setOpenSideActionBoard(false)}
          >
            <SVG src={ChevronRightIcon} />
          </button>
          <span
            style={{ width: 'calc(100% + 6rem)' }}
            className="absolute pin-b pin-r -mx-16 h-px bg-gray flex"
          />
        </div>
        <UserView
          user={formData}
          openSideActionBoard={openSideActionBoard}
          setOpenSideActionBoard={setOpenSideActionBoard}
        />

      </SideActionBoard>
    );
  }  
};


const UserView = ({ user, setOpenSideActionBoard, openSideActionBoard }) =>
  user && (
    <>
      <div className="sidebar-table overflow-y-scroll h-full px-8 py-8">
        <h1>
          {user.profile?.first_name || '---'} {user.profile?.last_name || ''}
        </h1>
        <table className="mt-12">
          <tbody>
            <h3 className="mb-4">Account Details</h3>
            <tr>
              <td>Email</td>
              <td>{user.email ? user.email : '---'}</td>
            </tr>
            <tr>
              <td>Origin</td>
              <td>{user.sign_up_origin ?? '---'}</td>
            </tr>
            <tr>
              <td>User Expiration Date</td>
              <td>
                {user.access_expiration_date
                  ? formatDate(user.access_expiration_date)
                  : '---'}
              </td>
            </tr>
            <tr>
              <td>Invitation sent on</td>
              <td>
                {user.user_invite?.invited_at
                  ? formatDate(user.user_invite?.invited_at)
                  : '---'}
              </td>
            </tr>
            <tr>
              <td>Invitation expires on</td>
              <td>
                {user.user_invite?.expires_at
                  ? formatDate(user.user_invite?.expires_at)
                  : '---'}
              </td>
            </tr>
            <tr>
              <td>CRA Internal Contact</td>
              <td>{user.user_invite?.invited_by_email || '---'}</td>
            </tr>
            <tr>
              <td>Company</td>
              <td>
                {user.profile?.company_name || user.profile?.hubspot?.company
                  ? user.profile?.company_name || user.profile?.hubspot?.company
                  : '---'}
              </td>
            </tr>
            <tr>
              <td>Cohorts</td>
              <td>
                {user.cohorts && user.cohorts.length > 0
                  ? user.cohorts.map((cohort, index) => (
                      <React.Fragment key={cohort.id}>
                        {cohort.name}
                        {index < user.cohorts.length - 1 && <br />}
                      </React.Fragment>
                    ))
                  : '---'}
              </td>
            </tr>
            <tr>
              <td>Alex Expiration Date</td>
              <td>
                {user.alex_expiration_date
                  ? formatDate(user.alex_expiration_date)
                  : '---'}
              </td>
            </tr>
            <tr>
              <td>Alex Available</td>
              <td>
                {user.is_alex_available
                  ? 'Yes'
                  : 'No'}
              </td>
            </tr>
            <tr>
              <td>Signed up on</td>
              <td>
                {user.confirmed_at ? formatDate(user.confirmed_at) : '---'}
              </td>
            </tr>
            <tr>
              <td>Last log in</td>
              <td>
                {user.last_sign_in_at
                  ? formatDate(user.last_sign_in_at)
                  : '---'}
              </td>
            </tr>
            <tr>
              <td>Email opt-in</td>
              <td>{user.profile?.opt_in ? 'Yes' : 'No'}</td>
            </tr>
            <tr>
              <td>User Access Type</td>
              <td>{user.user_access_type ? user.user_access_type : '---'}</td>
            </tr>
            <tr>
              <td>ALEX Access Type</td>
              <td>{user.alex_access_type ? user.alex_access_type : '---'}</td>
            </tr>
          </tbody>
        </table>

        {user.hubspot_properties && activeHubspotProperties && (
          <table className="mt-12">
            <h3 className="mb-4">HubSpot Properties</h3>
            <tbody>
              {Object.entries(user.hubspot_properties).map(([key, value]) => (
                <tr key={key}>
                  <td>{convertHubSpotLabel(key)}</td>
                  <td>
                    {key === 'alex_start_date' ? formatDate(value) : value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <h3 className="mt-12 mb-3">Modules</h3>
        <p className="mb-8 italic">
          Total Percent Completed: {user.percent_complete}%
        </p>
        {user.courses &&
          user.courses.map((course, courseIndex) => {
            const { enrolled_in: enrolledIn, title, behaviors } = course;
            return (
              <React.Fragment key={courseIndex}>
                <div className="flex items-center mb-4">
                  {enrolledIn ? (
                    <div className="rounded bg-green-lightest p-2 flex items-center justify-center mr-4">
                      <SVG className="svg-green" src={UnlockedIcon} />
                    </div>
                  ) : (
                    <div className="rounded bg-red-lightest p-2 flex items-center justify-center mr-4">
                      <SVG className="svg-red" src={LockedIcon} />
                    </div>
                  )}
                  <p>{title}</p>
                </div>
                {behaviors && (
                  <div
                    style={{ gap: '16px' }}
                    className="ml-4 pl-4 border-l border-gray mb-8 flex flex-col"
                  >
                    {behaviors.map((behavior, behaviorIndex) => {
                      const { title: behaviorTitle, completed } = behavior;

                      return (
                        <div key={behaviorIndex} className="flex items-center">
                          {completed ? (
                            <div className="rounded-full bg-green-lightest flex items-center justify-center p-1 mr-4 -my-2">
                              <SVG className="svg-green" src={CheckIcon} />
                            </div>
                          ) : (
                            <div className="w-8 mr-4" />
                          )}
                          <p>{behaviorTitle}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </React.Fragment>
            );
          })}
      </div>
    </>
  );

const UserEditForm = ({
  setOpenSideActionBoard,
  openSideActionBoard,
  user,
  cohorts,
  courses,
  alexAccessTypes,
  hubspotAccessTypes,
}) => {
  const [hasCraEmployee, setHasCraEmployee] = useState(false);
  const [craContactDirty, setCraContactDirty] = useState(false);
  const [formData, setFormData] = useState(
    user
      ? {
          id: user.id,
          first_name: user.profile?.first_name,
          last_name: user.profile?.last_name,
          email: user?.email,
          company_name:
            user.profile?.company_name || user.profile?.hubspot?.company,
          alex_expiration_date: user?.alex_expiration_date,
          is_alex_available: user?.is_alex_available,
          source: user.profile?.hubspot?.source,
          source_person: user.profile?.hubspot?.source_person,
          primary_lms_instance: user.profile?.hubspot?.primary_lms_instance,
          cohorts: user?.cohorts?.map(cohort => cohort.id) || [],
          courses: user?.courses || [],
          user_invite: user.user_invite
            ? {
                alex_access_type: user.alex_access_type,
                access_type: user.profile?.hubspot?.access_type,
                id: user.user_invite?.id,
                expires_at: user.user_invite?.expires_at,
                unlimited_gifting: user?.roles?.includes('unlimited_gifts'),
                invitation_access: user.user_invite?.access_type,
                skip_email: false,
                course_ids: user?.courses?.map(c => c.id) || [],
                valid_for_days: user.user_invite?.valid_for_days,
                expiration_type: user.user_invite?.expiration_type,
                length_type: user.user_invite?.length_type,
                invited_by_id: user.user_invite?.invited_by_id ?? null,
                invited_by_email: user.user_invite?.invited_by_email || '',
                discount_cents:
                  user?.user_invite?.discount_cents === 0
                    ? null
                    : 1000 - user?.user_invite?.discount_cents / 100,
              }
            : null,
        }
      : {}
  );

  useEffect(() => {
    setCraContactDirty(false);
    if (!user?.user_invite) return;
    setFormData((prev) => ({
      ...prev,
      user_invite: {
        ...prev.user_invite,
        invited_by_id: user.user_invite?.invited_by_id ?? null,
        invited_by_email: user.user_invite?.invited_by_email || '',
      },
    }));
  }, [user?.id, user?.user_invite?.invited_by_id, user?.user_invite?.invited_by_email]);

  const handleChange = (name, value, parent = null) => {
    setFormData((prevData) => {
      if (parent) {
        return {
          ...prevData,
          [parent]: {
            ...prevData[parent],
            [name]: value,
          },
        };
      }
      return { ...prevData, [name]: value };
    });
  };

  const queryClient = useQueryClient();

  const { setAlarm } = useAlarm();
  const { mutate, isLoading: mutationIsLoading } = useMutation(
    adminAPI.updateUser,
    {
      onSuccess: (data) => {
        if (data.error) {
          setAlarm({ type: 'error', message: data.message });
        } else {
          setOpenSideActionBoard();
          setAlarm({ type: 'success', message: data.message });
          queryClient.invalidateQueries('getUser');
          queryClient.invalidateQueries('getCohortUsers');
        }
      },
      onError: (error) => {
        setAlarm({ type: 'error', message: error.message });
      },
    }
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    let payload = { ...formData };
    if (formData.user_invite?.id && !craContactDirty && payload.user_invite) {
      const { invited_by_id: _ibid, invited_by_email: _ibe, ...restInvite } =
        payload.user_invite;
      payload = { ...payload, user_invite: restInvite };
    }
    const serializedData = JSON.stringify(payload);
    mutate(serializedData);
  };

  return (
    <>
      <div className="flex justify-between items-center relative pb-4 px-8">
        <button
          className="p-2 flex items-center -ml-2"
          type="button"
          onClick={() => setOpenSideActionBoard()}
        >
          <SVG src={ChevronRightIcon} />
        </button>
        <span
          style={{ width: 'calc(100% + 6rem)' }}
          className="absolute pin-b pin-r -mx-16 h-px bg-gray flex"
        />
      </div>
      <form
        className="overflow-y-scroll h-full px-8 py-8"
        onSubmit={handleSubmit}
      >
        <h1>
          {user.profile.first_name} {user.profile.last_name}
        </h1>
        <div style={{ gap: '16px' }} className="flex flex-col my-12">
          <FormInput
            label="Email"
            name="email"
            value={formData.email}
            type="text"
            handleChange={(e) => handleChange('email', e.target.value)}
          />
          <FormInput
            label="First Name"
            name="first_name"
            value={formData.first_name}
            type="text"
            handleChange={(e) => handleChange('first_name', e.target.value)}
          />
          <FormInput
            label="Last Name"
            name="last_name"
            value={formData.last_name}
            type="text"
            handleChange={(e) => handleChange('last_name', e.target.value)}
          />
          <FormInput
            label="Company"
            name="company"
            value={formData.company_name}
            type="text"
            handleChange={(e) => handleChange('company_name', e.target.value)}
          />
          <div>
            <CohortMultiSelect
              cohorts={cohorts || []}
              selectedCohorts={
                Array.isArray(formData.cohorts)
                  ? (cohorts || []).filter((cohort) => formData.cohorts.includes(cohort.value))
                  : []
              }
              onChangeCohortList={(selectedCohorts) =>
                handleChange(
                  'cohorts',
                  selectedCohorts.map((cohort) => cohort.value)
                )
              }
              // placeholder="Select cohorts..."
            />
          </div>
          <div className="mt-2">
            <p className="text-sm text-charcoal font-bold mb-2">
              Alex Available
            </p>
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="is_alex_available"
                  value="true"
                  checked={formData.is_alex_available === true}
                  onChange={(e) => handleChange('is_alex_available', e.target.value === 'true')}
                  className="mr-2"
                />
                <span className="text-sm">YES</span>
              </label>
              <label className="flex items-center ml-6">
                <input
                  type="radio"
                  name="is_alex_available"
                  value="false"
                  checked={formData.is_alex_available === false}
                  onChange={(e) => handleChange('is_alex_available', e.target.value === 'true')}
                  className="mr-2"
                />
                <span className="text-sm">NO</span>
              </label>
            </div>
          </div>
          <FormInput
            label="Alex Expiration"
            name="alex_expiration_date"
            value={
              formData.alex_expiration_date
                ? new Date(formData.alex_expiration_date)
                    .toISOString()
                    .split('T')[0]
                : ''
            }
            type="date"
            handleChange={(e) =>
              handleChange('alex_expiration_date', e.target.value)
            }
          />
          <div>
            <p className="text-sm text-charcoal  font-bold mb-4">
              Access Control
            </p>
            <div className="rounded-lg border-2 border-gray p-2 mt-4">
              <div className="flex items-center">
                <input
                  className="mx-4"
                  type="checkbox"
                  checked={hasCraEmployee}
                  onChange={() => setHasCraEmployee(!hasCraEmployee)}
                />
                <p>CRA Employee</p>
              </div>
            </div>
          </div>
        </div>
        <UserInviteForm
          formData={formData}
          courses={courses}
          handleChange={handleChange}
          alexAccessTypes={alexAccessTypes}
          hubspotAccessTypes={hubspotAccessTypes}
          cohorts={cohorts}
          application="ALD+ALEX"
          isEditUser={true}
          checkedAllUsers={false}
          onCraContactChange={() => setCraContactDirty(true)}
        />

        <button
          className="p-4 my-10  bg-purple text-white font-semibold rounded-lg"
          type="submit"
          data-testid="admin-user-edit-submit"
        >
          Update User
        </button>
      </form>
      {mutationIsLoading && <FormLoader />}
    </>
  );
};

export default SidebarUserEdit;